import { Quaternion } from '@technology/web3d';
import { AlignmentNavigationCapability } from '@trimble/web3d-capabilities';
import { StationPathIndexProvider } from '@trimble/web3d-providers';
import { SyncFn } from './sync-coordinator';
import {
  getActiveNavCap,
  isPathOfOtherMission,
  updateFrustum,
} from './sync-source-helpers';
import { getAlignmentSyncPosition } from './source-positions';
import { areQuaternionsEqual } from '@trimble/utilities';

const EPSILON = 1e-2;

export const syncAlignmentToAlignment: SyncFn = async (source, target) => {
  const sync = getAlignmentSyncPosition(source);
  if (!sync) return;

  const sourceViewer = source.viewerWrapper()?.viewer;
  const targetViewer = target.viewerWrapper()?.viewer;
  if (!sourceViewer || !targetViewer) return;

  const sourceCap = source.get(AlignmentNavigationCapability)();
  if (!sourceCap) return;

  const targetCap = getActiveNavCap(target);
  if (!(targetCap instanceof AlignmentNavigationCapability)) return;
  if (
    isPathOfOtherMission(
      sync,
      targetCap,
      target.get(StationPathIndexProvider)()
    )
  ) {
    return;
  }

  const chainage = sourceCap.chainage();
  if (chainage === undefined) return;

  const sourceIsInOverLength = sourceCap.isInOverLength();
  const targetChainage = targetCap.chainage();
  const targetIsInOverLength = targetCap.isInOverLength();

  const propagateOrientation =
    !sourceCap.crossSectionEnabled() && !targetCap.crossSectionEnabled();

  if (
    targetChainage === undefined ||
    Math.abs(chainage - targetChainage) > EPSILON ||
    sourceIsInOverLength !== targetIsInOverLength ||
    (propagateOrientation &&
      !areQuaternionsEqual(
        sourceViewer.camera.quaternion.toArray(),
        targetViewer.camera.quaternion.toArray()
      ))
  ) {
    await targetCap.navigateToWorldPosition(
      // todo @Guillaume: investigate why navigate with chainage and quaternion breaks the Bidirectional path nav test (no 2).
      sync.position.clone(),
      propagateOrientation
        ? new Quaternion(...sourceViewer.camera.quaternion.toArray())
        : undefined
    );
  }

  // Direction (forward/backward along the alignment) is NOT propagated
  // here.  PaneSyncer triggers this SyncFn only on camera/frustum
  // changes, and the trimbim tool flips `_direction` at orbit-end with
  // no follow-up camera motion — so this would miss pure-orbit flips.
  // `PathNavigationCoordinator.watchDirectionChange` owns that
  // propagation as a reactive effect on the source's direction signal.

  updateFrustum(
    target,
    sync.position,
    propagateOrientation ? sourceViewer.camera.quaternion.toArray() : null,
    null
  );
};
